/* The "right chart" for one field, for people who do not know BI terms: click "Add chart" next to a column
 * (or drag it onto an empty part of the page) and the designer picks the visual.
 *
 *   a number column            -> KPI card with its total
 *   a date / month / year      -> line chart of a number over time
 *   anything else (text, ids)  -> bar chart: a number per category (or a table if the data has no number)
 *
 * Pure: it only decides; StudioPage creates the measure / widget. See web/scripts/chart-from-field.check.ts. */

export interface FieldInfo {
  name: string;
  /** The column is a number (summed into a measure). */
  numeric?: boolean;
  /** Existing measure of the dataset dropped as-is. */
  isMeasure?: boolean;
  dataType?: string;
  label?: string;
}
export interface DatasetShape {
  /** Existing measures: name + the column they total. */
  measures: Array<{ name: string; display_name?: string | null; source_field?: string | null }>;
  /** Number columns that can be totalled (field_role === "measure"). */
  numericFields: Array<{ source_name: string; display_name: string }>;
}

export type MeasureSource = { kind: "existing"; name: string } | { kind: "total-of"; field: string; label: string } | null;
export interface ChartChoice {
  type: "kpi" | "line_chart" | "bar_chart" | "table";
  dimensions: string[];
  measure: MeasureSource;
  /** One plain sentence for the toast. */
  why: string;
}

const DATE_NAME = /(^|[\s_-])(date|day|month|year|period|week|quarter|time|created|updated)([\s_-]|$)/i;

export function isDateField(f: FieldInfo): boolean {
  const t = (f.dataType ?? "").toLowerCase();
  return /date|time/.test(t) || DATE_NAME.test(f.name) || DATE_NAME.test(f.label ?? "");
}

export function pickChart(f: FieldInfo, ds: DatasetShape): ChartChoice {
  const label = f.label || f.name;
  if (f.isMeasure) return { type: "kpi", dimensions: [], measure: { kind: "existing", name: f.name }, why: `A big number card for ${label}.` };
  if (f.numeric) return { type: "kpi", dimensions: [], measure: { kind: "total-of", field: f.name, label }, why: `A big number card showing the total ${label}.` };

  // Something to measure along the field: the first ready-made measure, else the first number column.
  const m: MeasureSource = ds.measures[0]
    ? { kind: "existing", name: ds.measures[0].name }
    : ds.numericFields[0]
      ? { kind: "total-of", field: ds.numericFields[0].source_name, label: ds.numericFields[0].display_name }
      : null;
  const what = ds.measures[0] ? ds.measures[0].display_name || ds.measures[0].name : ds.numericFields[0]?.display_name;
  if (!m) return { type: "table", dimensions: [f.name], measure: null, why: `A list of ${label} (this table has no number column to chart).` };
  if (isDateField(f)) return { type: "line_chart", dimensions: [f.name], measure: m, why: `A line chart of ${what} over ${label}.` };
  return { type: "bar_chart", dimensions: [f.name], measure: m, why: `A bar chart of ${what} for each ${label}.` };
}
